import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { resolveApiPath } from '@/services/api';
import { featureFlags } from '@/config/featureFlags';

/**
 * Environment modes:
 * - demo: Fully simulated, no real exchange connections
 * - testnet: Real exchange APIs in sandbox/testnet mode (fake money)
 * - prod: Production, real money trading
 */
export type EnvironmentMode = 'demo' | 'testnet' | 'prod';

export interface EnvironmentFeatures {
  console: boolean;
  automationsUi: boolean;
  strategyAdmin: boolean;
  aiAutopilot: boolean;
  aiSmartRouting: boolean;
}

interface EnvironmentContextType {
  /** Current environment mode */
  mode: EnvironmentMode;
  /** Human-readable label for the current mode */
  modeLabel: string;
  /** Whether this is demo mode (fully simulated) */
  isDemo: boolean;
  /** Whether this is testnet mode (real APIs, fake money) */
  isTestnet: boolean;
  /** Whether this is production mode (real money) */
  isProduction: boolean;
  /** API base URL */
  apiBaseUrl: string;
  /** Feature flags */
  features: EnvironmentFeatures;
}

export const EnvironmentContext = createContext<EnvironmentContextType | null>(null);

interface EnvironmentProviderProps {
  readonly children: ReactNode;
}

const getModeLabel = (mode: EnvironmentMode): string => {
  switch (mode) {
    case 'demo':
      return 'Demo';
    case 'testnet':
      return 'Testnet';
    case 'prod':
      return 'Production';
    default:
      return 'Unknown';
  }
};

export function EnvironmentProvider({ children }: EnvironmentProviderProps) {
  const [mode, setMode] = useState<EnvironmentMode>('demo');
  const [features, setFeatures] = useState<EnvironmentFeatures>({
    console: false,
    automationsUi: false,
    strategyAdmin: featureFlags.strategyAdmin,
    aiAutopilot: featureFlags.aiAutopilot,
    aiSmartRouting: featureFlags.smartRouting,
  });

  const getApiBaseUrl = (): string => {
    const baseUrl = import.meta.env.VITE_API_BASE_URL || '';
    return baseUrl;
  };

  useEffect(() => {
    let cancelled = false;

    const loadEnvironment = async () => {
      const platformEndpointEnv = import.meta.env.VITE_PLATFORM_ENV_URL?.replace(/\/$/, '') ?? '';
      const baseFromEnv = import.meta.env.VITE_API_BASE_URL?.replace(/\/$/, '') ?? '';
      const defaultEndpoint = resolveApiPath('/api/v1/environment');
      const endpoint = platformEndpointEnv
        ? `${platformEndpointEnv}/environment`
        : baseFromEnv
          ? `${baseFromEnv}/environment`
          : defaultEndpoint;

      try {
        const response = await fetch(endpoint, {
          credentials: 'include',
        });

        if (!response.ok) {
          throw new Error(`Unexpected status ${response.status}`);
        }

        const data = (await response.json()) as { mode?: string; features?: Record<string, unknown> };

        if (cancelled) {
          return;
        }

        // Normalize mode from backend
        const rawMode = (data.mode ?? 'demo').toLowerCase();
        let resolvedMode: EnvironmentMode = 'demo';
        if (rawMode === 'prod' || rawMode === 'production') {
          resolvedMode = 'prod';
        } else if (rawMode === 'testnet' || rawMode === 'sandbox' || rawMode === 'test' || rawMode === 'staging') {
          // staging is treated as testnet (non-demo, non-prod environment)
          resolvedMode = 'testnet';
        }

        setMode(resolvedMode);

        const featurePayload = data.features || {};
        setFeatures({
          console: Boolean(featurePayload.console),
          automationsUi: Boolean(
            featurePayload.automationsUi ?? featurePayload.automations_ui ?? false,
          ),
          strategyAdmin:
            Boolean(
              featurePayload.strategyAdmin ??
                featurePayload.strategy_admin ??
                featureFlags.strategyAdmin,
            ),
          aiAutopilot:
            Boolean(
              featurePayload.aiAutopilot ??
                featurePayload.ai_autopilot ??
                featureFlags.aiAutopilot,
            ),
          aiSmartRouting:
            Boolean(
              featurePayload.aiSmartRouting ??
                featurePayload.ai_smart_routing ??
                featureFlags.smartRouting,
            ),
        });
      } catch (error) {
        // Try fallback endpoint
        if (endpoint !== defaultEndpoint) {
          try {
            const response = await fetch(defaultEndpoint, { credentials: 'include' });
            if (!response.ok) {
              throw new Error(`Unexpected status ${response.status}`, { cause: error });
            }
            const data = (await response.json()) as { mode?: string; features?: Record<string, unknown> };
            if (!cancelled) {
              const rawMode = (data.mode ?? 'demo').toLowerCase();
              let resolvedMode: EnvironmentMode = 'demo';
              if (rawMode === 'prod' || rawMode === 'production') {
                resolvedMode = 'prod';
              } else if (rawMode === 'testnet' || rawMode === 'sandbox' || rawMode === 'test' || rawMode === 'staging') {
                // staging is treated as testnet (non-demo, non-prod environment)
                resolvedMode = 'testnet';
              }
              setMode(resolvedMode);

              const featurePayload = data.features || {};
              setFeatures({
                console: Boolean(featurePayload.console),
                automationsUi: Boolean(
                  featurePayload.automationsUi ?? featurePayload.automations_ui ?? false,
                ),
                strategyAdmin:
                  Boolean(
                    featurePayload.strategyAdmin ??
                      featurePayload.strategy_admin ??
                      featureFlags.strategyAdmin,
                  ),
                aiAutopilot:
                  Boolean(
                    featurePayload.aiAutopilot ??
                      featurePayload.ai_autopilot ??
                      featureFlags.aiAutopilot,
                  ),
                aiSmartRouting:
                  Boolean(
                    featurePayload.aiSmartRouting ??
                      featurePayload.ai_smart_routing ??
                      featureFlags.smartRouting,
                  ),
              });
            }
            return;
          } catch (fallbackError) {
            if (import.meta.env.DEV) {
              console.warn('Environment fallback fetch failed', fallbackError);
            }
          }
        }
        if (import.meta.env.DEV) {
          console.warn('Failed to load environment info', error);
        }
      }
    };

    loadEnvironment();

    return () => {
      cancelled = true;
    };
  }, []);

  const contextValue: EnvironmentContextType = {
    mode,
    modeLabel: getModeLabel(mode),
    isDemo: mode === 'demo',
    isTestnet: mode === 'testnet',
    isProduction: mode === 'prod',
    apiBaseUrl: getApiBaseUrl(),
    features,
  };

  return (
    <EnvironmentContext.Provider value={contextValue}>
      {children}
    </EnvironmentContext.Provider>
  );
}

export function useEnvironment() {
  const context = useContext(EnvironmentContext);
  if (!context) {
    throw new Error('useEnvironment must be used within EnvironmentProvider');
  }
  return context;
}
